import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  fetchOrders, fetchOrderItemsBetween, fetchMenu, fetchCategories,
  type Order, type OrderItem,
} from '@/lib/data';
import { useRestaurant } from '@/lib/session';

type ReportTab = 'sales' | 'items' | 'timeslots' | 'payments' | 'tax' | 'voids';

const tabs: { key: ReportTab; label: string; icon: string }[] = [
  { key: 'sales', label: 'Sales', icon: 'ri-line-chart-line' },
  { key: 'items', label: 'Items', icon: 'ri-restaurant-2-line' },
  { key: 'timeslots', label: 'Time Slots', icon: 'ri-time-line' },
  { key: 'payments', label: 'Payments', icon: 'ri-bank-card-line' },
  { key: 'tax', label: 'Tax (GST)', icon: 'ri-bill-line' },
  { key: 'voids', label: 'Voids', icon: 'ri-error-warning-line' },
];

const CHART_COLORS = [
  'oklch(var(--primary-400))',
  'oklch(var(--secondary-400))',
  'oklch(var(--accent-400))',
  'oklch(var(--background-500))',
  'oklch(var(--background-400))',
];

function formatCurrency(v: number): string {
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${Math.round(v)}`;
}

function formatFullCurrency(v: number): string {
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function hourLabel(h: number): string {
  const fmt = (x: number) => {
    const hr = ((x % 24) + 24) % 24;
    const ap = hr >= 12 ? 'PM' : 'AM';
    const d = hr % 12 === 0 ? 12 : hr % 12;
    return `${d} ${ap}`;
  };
  return `${fmt(h)}-${fmt(h + 1)}`;
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

/* ---------------- Computed row shapes ---------------- */

interface DailyRow {
  date: string; day: string;
  dineIn: number; takeaway: number; aggregator: number;
  total: number; orders: number; aov: number;
}

interface ItemRow {
  name: string; category: string;
  orders: number; revenue: number; avgPrice: number;
}

interface SlotRow { slot: string; hour: number; orders: number; sales: number; aov: number; }

interface PaymentRow {
  method: string; transactions: number; amount: number;
  percentage: number; avgTx: number;
}

function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const restaurant = useRestaurant();
  const [activeTab, setActiveTab] = useState<ReportTab>('sales');
  const [dateRange, setDateRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    return { from: toDateInput(from), to: toDateInput(to) };
  });
  const [itemSort, setItemSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'revenue', dir: 'desc' });
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[] | null>(null);
  const [categoryByItemName, setCategoryByItemName] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;
    let cancelled = false;
    setOrders(null);
    setOrderItems(null);
    setError('');
    const fromISO = new Date(`${dateRange.from}T00:00:00`).toISOString();
    const toEnd = new Date(`${dateRange.to}T00:00:00`);
    toEnd.setDate(toEnd.getDate() + 1);
    const toISO = toEnd.toISOString();
    Promise.all([
      fetchOrders(restaurant.id, { from: fromISO, to: toISO }),
      fetchOrderItemsBetween(restaurant.id, fromISO, toISO),
      fetchMenu(restaurant.id),
      fetchCategories(restaurant.id),
    ])
      .then(([o, it, menu, cats]) => {
        if (cancelled) return;
        const catName = new Map(cats.map((c) => [c.id, c.name]));
        const map: Record<string, string> = {};
        for (const m of menu) {
          map[m.name] = (m.category && catName.get(m.category)) || m.category || '—';
        }
        setCategoryByItemName(map);
        setOrders(o);
        setOrderItems(it);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load report data.');
      });
    return () => { cancelled = true; };
  }, [restaurant.id, dateRange.from, dateRange.to, reloadKey]);

  const loading = (orders === null || orderItems === null) && !error;

  const validOrders = useMemo(
    () => (orders ?? []).filter((o) => o.status !== 'cancelled'),
    [orders],
  );
  const cancelledOrders = useMemo(
    () => (orders ?? []).filter((o) => o.status === 'cancelled'),
    [orders],
  );

  const kpis = useMemo(() => {
    const totalSales = validOrders.reduce((s, o) => s + o.total, 0);
    const totalOrders = validOrders.length;
    return {
      totalSales,
      totalOrders,
      avgOrderValue: totalOrders ? totalSales / totalOrders : 0,
      totalTax: validOrders.reduce((s, o) => s + o.cgst + o.sgst, 0),
      totalVoids: cancelledOrders.reduce((s, o) => s + o.total, 0),
      voidCount: cancelledOrders.length,
    };
  }, [validOrders, cancelledOrders]);

  const dailyRows = useMemo<DailyRow[]>(() => {
    const byDate = new Map<string, DailyRow>();
    const start = new Date(`${dateRange.from}T00:00:00`);
    const end = new Date(`${dateRange.to}T00:00:00`);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = toDateInput(d);
      byDate.set(key, {
        date: key,
        day: d.toLocaleDateString('en-IN', { weekday: 'short' }),
        dineIn: 0, takeaway: 0, aggregator: 0, total: 0, orders: 0, aov: 0,
      });
    }
    for (const o of validOrders) {
      const key = toDateInput(new Date(o.placedAt));
      const row = byDate.get(key);
      if (!row) continue;
      if (o.type === 'Dine-in') row.dineIn += o.total;
      else if (o.type === 'Takeaway') row.takeaway += o.total;
      else row.aggregator += o.total;
      row.total += o.total;
      row.orders += 1;
    }
    const rows = [...byDate.values()];
    for (const r of rows) r.aov = r.orders ? r.total / r.orders : 0;
    return rows;
  }, [validOrders, dateRange.from, dateRange.to]);

  const itemRows = useMemo<ItemRow[]>(() => {
    const byName = new Map<string, ItemRow>();
    for (const it of orderItems ?? []) {
      const row = byName.get(it.name) ?? {
        name: it.name,
        category: categoryByItemName[it.name] ?? '—',
        orders: 0, revenue: 0, avgPrice: 0,
      };
      row.orders += it.qty;
      row.revenue += it.price * it.qty;
      byName.set(it.name, row);
    }
    const rows = [...byName.values()];
    for (const r of rows) r.avgPrice = r.orders ? r.revenue / r.orders : 0;
    return rows;
  }, [orderItems, categoryByItemName]);

  const slotRows = useMemo<SlotRow[]>(() => {
    const byHour = new Map<number, SlotRow>();
    for (const o of validOrders) {
      const h = new Date(o.placedAt).getHours();
      const row = byHour.get(h) ?? { slot: hourLabel(h), hour: h, orders: 0, sales: 0, aov: 0 };
      row.orders += 1;
      row.sales += o.total;
      byHour.set(h, row);
    }
    const rows = [...byHour.values()].sort((a, b) => a.hour - b.hour);
    for (const r of rows) r.aov = r.orders ? r.sales / r.orders : 0;
    return rows;
  }, [validOrders]);

  const paymentRows = useMemo<PaymentRow[]>(() => {
    const byMode = new Map<string, PaymentRow>();
    for (const o of validOrders) {
      const row = byMode.get(o.paymentMode) ?? {
        method: o.paymentMode, transactions: 0, amount: 0, percentage: 0, avgTx: 0,
      };
      row.transactions += 1;
      row.amount += o.total;
      byMode.set(o.paymentMode, row);
    }
    const rows = [...byMode.values()].sort((a, b) => b.amount - a.amount);
    for (const r of rows) {
      r.percentage = kpis.totalSales ? Math.round((r.amount / kpis.totalSales) * 100) : 0;
      r.avgTx = r.transactions ? r.amount / r.transactions : 0;
    }
    return rows;
  }, [validOrders, kpis.totalSales]);

  const tax = useMemo(() => {
    const cgst = validOrders.reduce((s, o) => s + o.cgst, 0);
    const sgst = validOrders.reduce((s, o) => s + o.sgst, 0);
    return { cgst, sgst, totalGst: cgst + sgst };
  }, [validOrders]);

  const sortedItems = useMemo(() => {
    const rows = [...itemRows];
    rows.sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[itemSort.key] as number;
      const bVal = (b as unknown as Record<string, unknown>)[itemSort.key] as number;
      return itemSort.dir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return rows;
  }, [itemRows, itemSort]);

  const handleItemSort = (key: string) => {
    setItemSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleExport = () => {
    const exportMenu = document.getElementById('export-menu');
    if (exportMenu) exportMenu.classList.toggle('hidden');
  };

  const closeExportMenu = () => {
    const exportMenu = document.getElementById('export-menu');
    if (exportMenu) exportMenu.classList.add('hidden');
  };

  const exportCsv = () => {
    closeExportMenu();
    const suffix = `${dateRange.from}_${dateRange.to}`;
    if (activeTab === 'sales') {
      downloadCsv(`sales_${suffix}.csv`,
        ['Date', 'Day', 'Dine-in', 'Takeaway', 'Aggregator', 'Total', 'Orders', 'Avg Order Value'],
        dailyRows.map((r) => [r.date, r.day, r.dineIn, r.takeaway, r.aggregator, r.total, r.orders, Math.round(r.aov)]));
    } else if (activeTab === 'items') {
      downloadCsv(`items_${suffix}.csv`,
        ['Item', 'Category', 'Qty Sold', 'Revenue', 'Avg Price'],
        sortedItems.map((r) => [r.name, r.category, r.orders, r.revenue, Math.round(r.avgPrice)]));
    } else if (activeTab === 'timeslots') {
      downloadCsv(`timeslots_${suffix}.csv`,
        ['Time Slot', 'Orders', 'Sales', 'Avg Order Value'],
        slotRows.map((r) => [r.slot, r.orders, r.sales, Math.round(r.aov)]));
    } else if (activeTab === 'payments') {
      downloadCsv(`payments_${suffix}.csv`,
        ['Method', 'Transactions', 'Amount', 'Share %', 'Avg / Txn'],
        paymentRows.map((r) => [r.method, r.transactions, r.amount, r.percentage, Math.round(r.avgTx)]));
    } else if (activeTab === 'tax') {
      downloadCsv(`tax_${suffix}.csv`,
        ['Tax Component', 'Rate', 'Amount'],
        [
          ['CGST', `${restaurant.gstRate / 2}%`, Math.round(tax.cgst)],
          ['SGST', `${restaurant.gstRate / 2}%`, Math.round(tax.sgst)],
          ['Total GST', `${restaurant.gstRate}%`, Math.round(tax.totalGst)],
        ]);
    } else {
      downloadCsv(`voids_${suffix}.csv`,
        ['Order ID', 'Type', 'Customer', 'Amount', 'Payment Mode', 'Placed At'],
        cancelledOrders.map((o) => [o.id, o.type, o.customer, o.total, o.paymentMode, o.placedAt]));
    }
  };

  const printReport = () => {
    closeExportMenu();
    window.print();
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground-800">Reports</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            {dateRange.from} to {dateRange.to} · {restaurant.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date inputs */}
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange((p) => ({ ...p, from: e.target.value }))}
              className="px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            />
            <span className="text-foreground-400 text-sm">to</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange((p) => ({ ...p, to: e.target.value }))}
              className="px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            />
          </div>
          {/* Export */}
          <div className="relative">
            <button
              onClick={handleExport}
              className="px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-2-line text-base"></i>
              </div>
              Export
            </button>
            <div
              id="export-menu"
              className="hidden absolute right-0 top-full mt-1 bg-background-50 border border-background-200 rounded-lg shadow-lg py-1 z-50 min-w-[160px]"
            >
              <button
                onClick={exportCsv}
                className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                Export as CSV
              </button>
              <button
                onClick={printReport}
                className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                Print Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-600 hover:text-foreground-800'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={tab.icon}></i>
            </div>
            {tab.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="bg-background-50 border border-background-200 rounded-lg py-20 text-center">
          <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
          <p className="text-sm text-foreground-500 mt-3">Loading report data…</p>
        </div>
      )}

      {error && !loading && (
        <div className="bg-background-50 border border-background-200 rounded-lg py-20 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl text-accent-500"></i>
          </div>
          <p className="text-sm text-foreground-500">{error}</p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-3 px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* KPI Row — visible on all tabs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: 'Total Sales', value: formatFullCurrency(kpis.totalSales) },
              { label: 'Orders', value: kpis.totalOrders.toString() },
              { label: 'Avg. Order Value', value: formatFullCurrency(kpis.avgOrderValue) },
              { label: 'Tax Collected', value: formatFullCurrency(kpis.totalTax) },
              { label: 'Voids', value: formatFullCurrency(kpis.totalVoids) },
            ].map((kpi) => (
              <div
                key={kpi.label}
                className="bg-background-50 border border-background-200 rounded-lg p-4 hover:border-background-300 transition-colors"
              >
                <p className="text-xs text-foreground-500 mb-1">{kpi.label}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-foreground-800">{kpi.value}</span>
                </div>
              </div>
            ))}
          </div>

          {(orders ?? []).length === 0 ? (
            <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-xl text-foreground-300"></i>
              </div>
              <p className="text-sm text-foreground-500">No orders in this date range</p>
            </div>
          ) : (
            <>
              {/* Tab Content */}
              {activeTab === 'sales' && <SalesTab rows={dailyRows} />}
              {activeTab === 'items' && (
                <ItemsTab items={sortedItems} onSort={handleItemSort} sortKey={itemSort.key} sortDir={itemSort.dir} />
              )}
              {activeTab === 'timeslots' && <TimeSlotsTab rows={slotRows} />}
              {activeTab === 'payments' && <PaymentsTab rows={paymentRows} />}
              {activeTab === 'tax' && (
                <TaxTab tax={tax} gstRate={restaurant.gstRate} totalSales={kpis.totalSales} />
              )}
              {activeTab === 'voids' && (
                <VoidsTab cancelled={cancelledOrders} totalSales={kpis.totalSales} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ──────────────── Sales Tab ──────────────── */

function SalesTab({ rows }: { rows: DailyRow[] }) {
  return (
    <div className="space-y-5">
      {/* Sales trend chart */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-foreground-800">Daily Sales Trend</h4>
          <div className="flex items-center gap-3">
            {[
              { label: 'Dine-in', cls: 'bg-primary-400' },
              { label: 'Takeaway', cls: 'bg-secondary-400' },
              { label: 'Aggregator', cls: 'bg-accent-400' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-sm ${item.cls}`}></div>
                <span className="text-xs text-foreground-500">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} barGap={0} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200))" vertical={false} />
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: 'oklch(var(--foreground-500))', fontSize: 12 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: 'oklch(var(--foreground-500))', fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'oklch(var(--background-50))',
                  border: '1px solid oklch(var(--background-200))',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                formatter={(value: number) => [formatFullCurrency(value), '']}
              />
              <Bar dataKey="dineIn" name="Dine-in" fill="oklch(var(--primary-400))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="takeaway" name="Takeaway" fill="oklch(var(--secondary-400))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="aggregator" name="Aggregator" fill="oklch(var(--accent-400))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Daily breakdown table */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-4 md:px-5 py-3 border-b border-background-200">
          <h4 className="text-sm font-semibold text-foreground-800">Daily Breakdown</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Dine-in</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Takeaway</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Aggregator</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Orders</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Avg Order</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {rows.map((row) => (
                <tr key={row.date} className="hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground-800">{row.day}</span>
                    <span className="text-xs text-foreground-400 ml-2">{row.date}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-foreground-700">{formatFullCurrency(row.dineIn)}</td>
                  <td className="px-4 py-3 text-right text-foreground-700">{formatFullCurrency(row.takeaway)}</td>
                  <td className="px-4 py-3 text-right text-foreground-700">{formatFullCurrency(row.aggregator)}</td>
                  <td className="px-4 py-3 text-right text-foreground-700">{row.orders}</td>
                  <td className="px-4 py-3 text-right text-foreground-700">{formatFullCurrency(row.aov)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground-800">{formatFullCurrency(row.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-background-100 border-t border-background-200">
              <tr>
                <td className="px-4 py-3 font-semibold text-foreground-800">Range Total</td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-800">
                  {formatFullCurrency(rows.reduce((s, r) => s + r.dineIn, 0))}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-800">
                  {formatFullCurrency(rows.reduce((s, r) => s + r.takeaway, 0))}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-800">
                  {formatFullCurrency(rows.reduce((s, r) => s + r.aggregator, 0))}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-800">
                  {rows.reduce((s, r) => s + r.orders, 0)}
                </td>
                <td></td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-800">
                  {formatFullCurrency(rows.reduce((s, r) => s + r.total, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Items Tab ──────────────── */

function ItemsTab({
  items, onSort, sortKey, sortDir,
}: {
  items: ItemRow[];
  onSort: (key: string) => void;
  sortKey: string;
  sortDir: 'asc' | 'desc';
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
      <div className="px-4 md:px-5 py-3 border-b border-background-200 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground-800">Item-wise Performance</h4>
        <span className="text-xs text-foreground-400">{items.length} items</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-background-100 border-b border-background-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">#</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Item</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Category</th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer hover:text-foreground-700"
                onClick={() => onSort('orders')}
              >
                Qty Sold {sortKey === 'orders' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer hover:text-foreground-700"
                onClick={() => onSort('revenue')}
              >
                Revenue {sortKey === 'revenue' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Avg Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200">
            {items.map((item, i) => (
              <tr key={item.name} className="hover:bg-background-100/50 transition-colors">
                <td className="px-4 py-3">
                  <span className="text-xs font-semibold text-foreground-400">{i + 1}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-medium text-foreground-800">{item.name}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                    {item.category}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-foreground-700">{item.orders}</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-800">{formatFullCurrency(item.revenue)}</td>
                <td className="px-4 py-3 text-right text-foreground-600">{formatFullCurrency(item.avgPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-sm text-foreground-500">No items sold in this date range</p>
        </div>
      )}
    </div>
  );
}

/* ──────────────── Time Slots Tab ──────────────── */

function TimeSlotsTab({ rows }: { rows: SlotRow[] }) {
  const maxSales = rows.length ? Math.max(...rows.map((d) => d.sales)) : 0;
  const peak = rows.find((d) => d.sales === maxSales);

  return (
    <div className="space-y-5">
      {/* Hourly chart */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
        <h4 className="text-sm font-semibold text-foreground-800 mb-4">Hourly Sales Distribution</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200))" vertical={false} />
              <XAxis dataKey="slot" axisLine={false} tickLine={false} tick={{ fill: 'oklch(var(--foreground-500))', fontSize: 11 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: 'oklch(var(--foreground-500))', fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'oklch(var(--background-50))',
                  border: '1px solid oklch(var(--background-200))',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                formatter={(value: number, name: string) => {
                  if (name === 'sales') return [formatFullCurrency(value), 'Sales'];
                  return [value, name];
                }}
              />
              <Bar dataKey="sales" name="sales" radius={[4, 4, 0, 0]}>
                {rows.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={entry.sales === maxSales ? 'oklch(var(--accent-500))' : 'oklch(var(--primary-400))'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {peak && (
          <p className="text-xs text-foreground-400 mt-2 text-center">
            Peak hour: <span className="font-semibold text-accent-500">{peak.slot}</span> — {formatFullCurrency(peak.sales)} ({peak.orders} orders)
          </p>
        )}
      </div>

      {/* Time slot table */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-4 md:px-5 py-3 border-b border-background-200">
          <h4 className="text-sm font-semibold text-foreground-800">Hourly Breakdown</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Time Slot</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Orders</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Sales</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Avg Order Value</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Load</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {rows.map((row) => {
                const ratio = maxSales ? row.sales / maxSales : 0;
                const isPeak = maxSales > 0 && row.sales === maxSales;
                return (
                  <tr key={row.slot} className={`hover:bg-background-100/50 transition-colors ${isPeak ? 'bg-accent-100/30' : ''}`}>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${isPeak ? 'text-accent-800' : 'text-foreground-800'}`}>
                        {row.slot}
                      </span>
                      {isPeak && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-accent-500 text-background-50 font-medium">Peak</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground-700">{row.orders}</td>
                    <td className="px-4 py-3 text-right font-medium text-foreground-800">{formatFullCurrency(row.sales)}</td>
                    <td className="px-4 py-3 text-right text-foreground-600">{formatFullCurrency(row.aov)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden max-w-[80px]">
                          <div
                            className={`h-full rounded-full transition-all ${isPeak ? 'bg-accent-500' : 'bg-primary-400'}`}
                            style={{ width: `${Math.round(ratio * 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-foreground-400 w-8">{Math.round(ratio * 100)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Payments Tab ──────────────── */

function PaymentsTab({ rows }: { rows: PaymentRow[] }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Pie chart */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <h4 className="text-sm font-semibold text-foreground-800 mb-3">Payment Mode Split</h4>
          <div className="flex items-center gap-4">
            <div className="w-44 h-44 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rows}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={72}
                    paddingAngle={2}
                    dataKey="amount"
                    strokeWidth={0}
                  >
                    {rows.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'oklch(var(--background-50))',
                      border: '1px solid oklch(var(--background-200))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value: number) => [formatFullCurrency(value), '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2.5">
              {rows.map((item, i) => (
                <div key={item.method} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}></div>
                    <span className="text-xs text-foreground-600">{item.method}</span>
                  </div>
                  <span className="text-xs font-medium text-foreground-800">{item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-3">
          {rows.map((item, i) => (
            <div key={item.method} className="bg-background-50 border border-background-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}></div>
                <span className="text-xs font-medium text-foreground-800">{item.method}</span>
              </div>
              <p className="text-xl font-bold text-foreground-800 mb-1">{formatCurrency(item.amount)}</p>
              <div className="flex items-center gap-3 text-xs text-foreground-500">
                <span>{item.transactions} txns</span>
                <span>Avg {formatFullCurrency(item.avgTx)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-4 md:px-5 py-3 border-b border-background-200">
          <h4 className="text-sm font-semibold text-foreground-800">Payment Breakdown</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Method</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Transactions</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Amount</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Share</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Avg / Txn</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {rows.map((item) => (
                <tr key={item.method} className="hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground-800">{item.method}</td>
                  <td className="px-4 py-3 text-right text-foreground-700">{item.transactions}</td>
                  <td className="px-4 py-3 text-right font-medium text-foreground-800">{formatFullCurrency(item.amount)}</td>
                  <td className="px-4 py-3 text-right text-foreground-600">{item.percentage}%</td>
                  <td className="px-4 py-3 text-right text-foreground-600">{formatFullCurrency(item.avgTx)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Tax Tab ──────────────── */

function TaxTab({
  tax, gstRate, totalSales,
}: {
  tax: { cgst: number; sgst: number; totalGst: number };
  gstRate: number;
  totalSales: number;
}) {
  const pctOfSales = (v: number) => (totalSales ? `${((v / totalSales) * 100).toFixed(1)}%` : '—');
  const halfRate = gstRate / 2;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="text-xs text-foreground-500 mb-1">CGST ({halfRate}%)</p>
          <p className="text-2xl font-bold text-foreground-800">{formatFullCurrency(tax.cgst)}</p>
          <p className="text-xs text-foreground-400 mt-1">Central GST</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="text-xs text-foreground-500 mb-1">SGST ({halfRate}%)</p>
          <p className="text-2xl font-bold text-foreground-800">{formatFullCurrency(tax.sgst)}</p>
          <p className="text-xs text-foreground-400 mt-1">State GST</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="text-xs text-foreground-500 mb-1">Total GST</p>
          <p className="text-2xl font-bold text-primary-600">{formatFullCurrency(tax.totalGst)}</p>
          <p className="text-xs text-foreground-400 mt-1">CGST + SGST</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-4 md:px-5 py-3 border-b border-background-200">
          <h4 className="text-sm font-semibold text-foreground-800">Tax Summary</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tax Component</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Rate</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Amount</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">% of Sales</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              <tr className="hover:bg-background-100/50">
                <td className="px-4 py-3 font-medium text-foreground-800">CGST</td>
                <td className="px-4 py-3 text-right text-foreground-600">{halfRate}%</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-800">{formatFullCurrency(tax.cgst)}</td>
                <td className="px-4 py-3 text-right text-foreground-600">{pctOfSales(tax.cgst)}</td>
              </tr>
              <tr className="hover:bg-background-100/50">
                <td className="px-4 py-3 font-medium text-foreground-800">SGST</td>
                <td className="px-4 py-3 text-right text-foreground-600">{halfRate}%</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-800">{formatFullCurrency(tax.sgst)}</td>
                <td className="px-4 py-3 text-right text-foreground-600">{pctOfSales(tax.sgst)}</td>
              </tr>
              <tr className="bg-background-100/50">
                <td className="px-4 py-3 font-semibold text-foreground-800">Total GST</td>
                <td className="px-4 py-3 text-right text-foreground-600">{gstRate}%</td>
                <td className="px-4 py-3 text-right font-semibold text-primary-600">{formatFullCurrency(tax.totalGst)}</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-600">{pctOfSales(tax.totalGst)}</td>
              </tr>
            </tbody>
            <tfoot className="bg-background-100 border-t border-background-200">
              <tr>
                <td className="px-4 py-3 font-semibold text-foreground-800">Total Tax Collected</td>
                <td></td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-800 text-base">{formatFullCurrency(tax.totalGst)}</td>
                <td className="px-4 py-3 text-right font-medium text-foreground-600">{pctOfSales(tax.totalGst)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Voids Tab ──────────────── */

function VoidsTab({ cancelled, totalSales }: { cancelled: Order[]; totalSales: number }) {
  const voidAmount = cancelled.reduce((s, o) => s + o.total, 0);
  const pctOfSales = totalSales ? ((voidAmount / totalSales) * 100).toFixed(2) : '0';

  return (
    <div className="space-y-5">
      {/* Summary row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Voids summary */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-close-circle-line text-accent-500 text-lg"></i>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground-800">Voids (Cancelled Orders)</h4>
              <p className="text-xs text-foreground-400">{cancelled.length} cancelled · {pctOfSales}% of sales</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-accent-500">{formatFullCurrency(voidAmount)}</p>
        </div>

        {/* Discounts — not tracked in this schema */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-discount-percent-line text-secondary-500 text-lg"></i>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground-800">Discounts</h4>
              <p className="text-xs text-foreground-400">Not tracked yet</p>
            </div>
          </div>
          <p className="text-sm text-foreground-500">Discount tracking is not enabled for this restaurant, so there is no discount data to report.</p>
        </div>
      </div>

      {/* Cancelled orders table */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-4 md:px-5 py-3 border-b border-background-200">
          <h4 className="text-sm font-semibold text-foreground-800">Cancelled Orders</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Order ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Customer</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Payment</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {cancelled.map((o) => (
                <tr key={o.id} className="hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-accent-500 font-medium">{shortId(o.id)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                      {o.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-700">{o.customer}</td>
                  <td className="px-4 py-3 text-right font-medium text-accent-500">{formatFullCurrency(o.total)}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600">{o.paymentMode}</td>
                  <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">
                    {new Date(o.placedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cancelled.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-foreground-500">No cancelled orders in this date range</p>
          </div>
        )}
      </div>
    </div>
  );
}

